import React from 'react';
import PropTypes from 'prop-types';
import { resolveTestId } from '@cvent/nucleus-test-automation';

const SIZES = [
  { class: 'xs', name: 'extraSmall', hide: 'isExtraSmallHidden' },
  { class: 'sm', name: 'small', hide: 'isSmallHidden' },
  { class: 'md', name: 'medium', hide: 'isMediumHidden' },
  { class: 'lg', name: 'large', hide: 'isLargeHidden' }
];


/**
* The bottom level component that contains any type of child content.
*/
const Column = (props, context) => {
  const { children, className = '' } = props;
  const { grid } = context;
  const classKeys = [];
  SIZES.forEach((entry) => {
    const size = props[entry.name];
    if (size || size > 0) {
      classKeys.push(`col-${entry.class}-${size}`);
    }
    const pull = props[entry.name + 'Pull'];
    if (pull || pull > 0) {
      classKeys.push(`col-${entry.class}-pull-${pull}`);
    }
    const offset = props[entry.name + 'Offset'];
    if (offset || offset >= 0) {
      classKeys.push(`col-${entry.class}-offset-${offset}`);
    }
    const push = props[entry.name + 'Push'];
    if (push || push > 0) {
      classKeys.push(`col-${entry.class}-push-${push}`);
    }
    if (props[entry.hide]) {
      classKeys.push(`hidden-${entry.class}`);
    }
  });
  const columnClass = classKeys.reduce((prev, value) => {
    return `${prev} ${grid[value]}`;
  }, `${grid.col} ${className} `);
  return (
    <div className={columnClass} style={props.style} {...resolveTestId(props)}>
      {children}
    </div>);
};

Column.propTypes = {
  /** The container's children */
  children: PropTypes.node,
  /** container css class */
  className: PropTypes.string,
  /** The number of columns you wish to span for Extra Small devices */
  extraSmall: PropTypes.number,
  /** Move columns to the right for Extra Small devices */
  extraSmallOffset: PropTypes.number,
  /** Change the order of grid columns to the right for Extra Small devices */
  extraSmallPush: PropTypes.number,
  /** Change the order of grid columns to the left for Extra Small devices */
  extraSmallPull: PropTypes.number,
  /** Hide column on Extra Small Devices */
  isExtraSmallHidden: PropTypes.bool,
  /** The number of columns you wish to span for Small devices */
  small: PropTypes.number,
  /** Move columns to the right for Small devices */
  smallOffset: PropTypes.number,
  /** Change the order of grid columns to the right for Small devices */
  smallPush: PropTypes.number,
  /** Change the order of grid columns to the left for Small devices */
  smallPull: PropTypes.number,
  /** Hide column on Small Devices */
  isSmallHidden: PropTypes.bool,
  /** The number of columns you wish to span for Medium devices */
  medium: PropTypes.number,
  /** Move columns to the right for Medium devices */
  mediumOffset: PropTypes.number,
  /** Change the order of grid columns to the right for Medium devices */
  mediumPush: PropTypes.number,
  /** Change the order of grid columns to the left for Medium devices */
  mediumPull: PropTypes.number,
  /** Hide column on Medium Devices */
  isMediumHidden: PropTypes.bool,
  /** The number of columns you wish to span for Large devices */
  large: PropTypes.number,
  /** Move columns to the right for Large devices */
  largeOffset: PropTypes.number,
  /** Change the order of grid columns to the right */
  largePush: PropTypes.number,
  /** Change the order of grid columns to the left for Large devices */
  largePull: PropTypes.number,
  /** Hide column on Large Devices */
  isLargeHidden: PropTypes.bool
};
Column.defaultProps = {
  extraSmall: 0,
  extraSmallOffset: 0,
  extraSmallPush: 0,
  extraSmallPull: 0,
  isExtraSmallHidden: false,
  small: 0,
  smallOffset: 0,
  smallPush: 0,
  smallPull: 0,
  isSmallHidden: false,
  medium: 0,
  mediumOffset: 0,
  mediumPush: 0,
  mediumPull: 0,
  isMediumHidden: false,
  large: 0,
  largeOffset: 0,
  largePull: 0,
  isLargeHidden: false
};
Column.contextTypes = {
  grid: PropTypes.object
};
Column.displayName = 'Column';

export default Column;
